/**
 * EmbedSchedule.js - Minimal iframe-friendly league schedule + standings view
 *
 * Route: /embed/:slug (public, no auth)
 * Fetches from GET /public/:slug (same endpoint as PublicLeaguePage)
 * Renders schedule by week and standings table — no header, footer, or nav
 * Posts height to parent window for auto-sizing iframes
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { getSportConfig } from '../utils/sportConfig';

function EmbedSchedule() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('schedule');
  const containerRef = useRef(null);

  useEffect(() => {
    const loadLeague = async () => {
      try {
        setLoading(true);
        const response = await api.getPublicLeague(slug);
        setData(response);
      } catch (err) {
        setError(err.message || 'League not found');
      } finally {
        setLoading(false);
      }
    };
    loadLeague();
  }, [slug]);

  // Post height to parent window for iframe auto-sizing
  useEffect(() => {
    const postHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'leaguemaker-height', height }, '*');
    };

    // Post after render and on resize
    postHeight();
    const timer = setInterval(postHeight, 2000);
    window.addEventListener('resize', postHeight);

    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', postHeight);
    };
  }, [data, activeTab]);

  // Override body styles for clean embed appearance
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.background = '#ffffff';
    document.body.style.overflow = 'auto';
    document.documentElement.style.background = '#ffffff';
    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.background = '';
      document.body.style.overflow = '';
      document.documentElement.style.background = '';
    };
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading schedule...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  const { league, standings, schedule } = data;
  const sportConfig = getSportConfig(league.sport);
  const publicUrl = `${window.location.origin}/public/${slug}`;
  const brandColor = league.primaryColor || '#667eea';

  return (
    <div ref={containerRef} style={styles.container}>
      {/* League header */}
      <div style={{
        ...styles.header,
        borderBottomColor: brandColor
      }}>
        {league.logoUrl && (
          <img
            src={league.logoUrl}
            alt={`${league.name} logo`}
            style={{ maxHeight: 60, maxWidth: 200, marginBottom: 8, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <h1 style={styles.leagueName}>{league.name}</h1>
        {league.description && (
          <span style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginTop: 2, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.4 }}>
            {league.description}
          </span>
        )}
        {league.seasonYear && (
          <span style={styles.season}>{league.seasonYear} Season</span>
        )}
      </div>

      {/* Tab navigation */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('schedule')}
          style={{
            ...styles.tab,
            ...(activeTab === 'schedule' ? { ...styles.tabActive, color: brandColor, borderBottomColor: brandColor } : {}),
          }}
        >
          Schedule
        </button>
        {league.showStandings !== false && standings && standings.length > 0 && (
          <button
            onClick={() => setActiveTab('standings')}
            style={{
              ...styles.tab,
              ...(activeTab === 'standings' ? { ...styles.tabActive, color: brandColor, borderBottomColor: brandColor } : {}),
            }}
          >
            Standings
          </button>
        )}
      </div>

      {/* Schedule tab */}
      {activeTab === 'schedule' && (
        <div style={styles.content}>
          {schedule && schedule.length > 0 ? (
            schedule.map((week) => (
              <div key={week.week} style={styles.weekCard}>
                <div style={{
                  ...styles.weekHeader,
                  background: `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}cc 100%)`
                }}>
                  <span style={styles.weekLabel}>Week {week.week}</span>
                  <span style={styles.weekDate}>{formatDate(week.date)}</span>
                </div>
                <div>
                  {week.games.map((game, i) => (
                    <div
                      key={i}
                      style={{
                        ...styles.gameRow,
                        borderBottom: i < week.games.length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}
                    >
                      <span style={styles.gameTime}>{game.time}</span>
                      <div style={styles.matchup}>
                        <span style={{
                          ...styles.teamName,
                          fontWeight: game.played && game.team1Score > game.team2Score ? 700 : 400,
                        }}>
                          {game.team1}
                        </span>
                        {game.status === 'forfeit' ? (
                          <span style={{ ...styles.score, color: '#e67e22' }}>
                            FORFEIT{game.forfeitTeam ? ` (${game.forfeitTeam})` : ''}
                          </span>
                        ) : game.status === 'cancelled' ? (
                          <span style={{ ...styles.score, color: '#dc3545' }}>
                            CANCELLED
                          </span>
                        ) : game.status === 'postponed' ? (
                          <span style={{ ...styles.score, color: '#856404' }}>
                            POSTPONED
                          </span>
                        ) : game.played ? (
                          sportConfig.winLossOnly ? (
                            <span style={{
                              ...styles.score,
                              color: game.team1Score === game.team2Score ? '#6c757d' : '#28a745',
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}>
                              {game.team1Score > game.team2Score
                                ? `Win \u2014 ${game.team1}`
                                : game.team2Score > game.team1Score
                                  ? `Win \u2014 ${game.team2}`
                                  : 'Draw'}
                            </span>
                          ) : (
                            <span style={{ ...styles.score, color: brandColor }}>
                              {game.team1Score} — {game.team2Score}
                            </span>
                          )
                        ) : (
                          <span style={styles.vs}>vs</span>
                        )}
                        <span style={{
                          ...styles.teamName,
                          fontWeight: game.played && game.team2Score > game.team1Score ? 700 : 400,
                        }}>
                          {game.team2}
                        </span>
                      </div>
                      {game.venue && (
                        <span style={styles.venueText}>{game.venue.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p style={styles.emptyText}>No games scheduled yet.</p>
          )}
        </div>
      )}

      {/* Standings tab */}
      {activeTab === 'standings' && (
        <div style={styles.content}>
          {standings && standings.length > 0 ? (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...styles.thRank }}>#</th>
                    <th style={{ ...styles.th, ...styles.thTeam }}>Team</th>
                    <th style={styles.th}>GP</th>
                    <th style={styles.th}>W</th>
                    <th style={styles.th}>L</th>
                    {sportConfig.allowTies && <th style={styles.th}>T</th>}
                    <th style={{ ...styles.th, fontWeight: 700 }}>PTS</th>
                    {!sportConfig.winLossOnly && <th style={{ ...styles.th, ...styles.thStat }}>{sportConfig.statLabels.goalsFor}</th>}
                    {!sportConfig.winLossOnly && <th style={{ ...styles.th, ...styles.thStat }}>{sportConfig.statLabels.goalsAgainst}</th>}
                    {!sportConfig.winLossOnly && <th style={{ ...styles.th, ...styles.thStat }}>{sportConfig.statLabels.goalDifferential}</th>}
                  </tr>
                </thead>
                <tbody>
                  {standings.map((team, index) => (
                    <tr key={team.id} style={index % 2 === 1 ? styles.trStripe : {}}>
                      <td style={{ ...styles.td, ...styles.tdRank }}>{index + 1}</td>
                      <td style={{ ...styles.td, ...styles.tdTeam }}>{team.name}</td>
                      <td style={styles.td}>{team.gamesPlayed}</td>
                      <td style={styles.td}>{team.wins}</td>
                      <td style={styles.td}>{team.losses}</td>
                      {sportConfig.allowTies && <td style={styles.td}>{team.ties}</td>}
                      <td style={{ ...styles.td, fontWeight: 700 }}>{team.points}</td>
                      {!sportConfig.winLossOnly && <td style={{ ...styles.td, ...styles.tdStat }}>{team.goalsFor}</td>}
                      {!sportConfig.winLossOnly && <td style={{ ...styles.td, ...styles.tdStat }}>{team.goalsAgainst}</td>}
                      {!sportConfig.winLossOnly && (
                        <td style={{ ...styles.td, ...styles.tdStat }}>
                          {team.goalDifferential > 0 ? '+' : ''}{team.goalDifferential}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={styles.emptyText}>No standings available.</p>
          )}
        </div>
      )}

      {/* Powered by footer */}
      <div style={styles.footer}>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
          Powered by LeagueMaker
        </a>
      </div>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    maxWidth: 800,
    margin: '0 auto',
    padding: '16px',
    color: '#333',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  loading: {
    textAlign: 'center',
    padding: '40px 16px',
    color: '#888',
    fontSize: '15px',
  },
  error: {
    textAlign: 'center',
    padding: '40px 16px',
    color: '#dc3545',
    fontSize: '15px',
  },
  header: {
    textAlign: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '2px solid #667eea',
  },
  leagueName: {
    margin: 0,
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#333',
  },
  season: {
    fontSize: '0.85rem',
    color: '#888',
    marginTop: 2,
    display: 'block',
  },
  tabs: {
    display: 'flex',
    gap: 0,
    marginBottom: 16,
    borderBottom: '2px solid #e8e8e8',
  },
  tab: {
    padding: '8px 20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#888',
    borderBottom: '2px solid transparent',
    marginBottom: -2,
    transition: 'color 0.15s, border-color 0.15s',
  },
  tabActive: {
    color: '#667eea',
    borderBottomColor: '#667eea',
    fontWeight: 600,
  },
  content: {
    minHeight: 100,
  },
  weekCard: {
    marginBottom: 16,
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    overflow: 'hidden',
  },
  weekHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 14px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
  },
  weekLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  weekDate: {
    fontSize: '0.8rem',
    opacity: 0.9,
  },
  gameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    flexWrap: 'wrap',
  },
  gameTime: {
    fontSize: '0.8rem',
    color: '#888',
    minWidth: 65,
    flexShrink: 0,
  },
  matchup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  teamName: {
    fontSize: '0.9rem',
    color: '#333',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  vs: {
    fontSize: '0.75rem',
    color: '#aaa',
    fontWeight: 500,
    flexShrink: 0,
  },
  score: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#667eea',
    flexShrink: 0,
  },
  venueText: {
    fontSize: '0.75rem',
    color: '#aaa',
    width: '100%',
    paddingLeft: 77,
    marginTop: -4,
  },
  emptyText: {
    textAlign: 'center',
    color: '#aaa',
    padding: '30px 16px',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
  },
  th: {
    padding: '8px 10px',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '2px solid #e8e8e8',
    background: '#f9fafb',
    whiteSpace: 'nowrap',
  },
  thRank: { width: 30, textAlign: 'center' },
  thTeam: { textAlign: 'left', minWidth: 100 },
  thStat: {},
  td: {
    padding: '8px 10px',
    textAlign: 'center',
    borderBottom: '1px solid #f0f0f0',
    whiteSpace: 'nowrap',
  },
  tdRank: { textAlign: 'center', fontWeight: 600, color: '#888' },
  tdTeam: { textAlign: 'left', fontWeight: 500 },
  tdStat: {},
  trStripe: {
    background: '#f9fafb',
  },
  footer: {
    textAlign: 'center',
    padding: '16px 0 8px',
    marginTop: 16,
    borderTop: '1px solid #e8e8e8',
  },
  footerLink: {
    fontSize: '0.75rem',
    color: '#aaa',
    textDecoration: 'none',
  },
};

export default EmbedSchedule;
